<template>
    <div class="search-history-view">
        <!-- 标题, 搜索 -->
        <TitleSearch
            :title="$t('history.' + currentTitle)"
            :tips="currentProject === 'analytics' ? $t('history.analytics_tips') : ''"
            @searchHandler="searchHandler"
        />
        <!-- 头部切换 -->
        <Tabs
            v-model="activeTab"
            @tab-click="handleClick"
            :class="{'search-history-view_login-tab':isLogin}"
        >
            <template v-for="item in currentTab">
                <TabPane
                    :key="item"
                    :lazy="true"
                    :label="$t('history.filter.' + item)"
                    :name="item"
                >
                    <!-- 删除 -->
                    <div
                        class="search-history-view__delete-all"
                        v-if="result && result.length"
                        @click="openDeleteAllDialog"
                    >
                        <PtIcon icon="SolidDelete" />
                        {{ $t('history.delete.' + isDeleteKey) }}
                    </div>

                    <!-- 排序&筛选 -->
                    <div class="search-history-view__sort-filter">
                        <ChemicalSortDropDown
                            :options="chemicalOptions"
                            :defaultType="historyType"
                            v-if="isShowAllType"
                            @allTypeChangeHandler="allTypeChangeHandler"
                        />
                        <SortDropdown
                            :options="options"
                            :defaultDate="defaultDate"
                            @dropdownChangeHandler="dropdownChangeHandler"
                        />
                        <!-- 只有search模块有搜索 -->
                        <div
                            class="history-query-title-search__search"
                            v-if="showSearchInput"
                        >
                            <input
                                type="text"
                                v-model="keyword"
                                :placeholder="placeholderText"
                                @keyup.enter="searchHandler"
                                maxlength="500"
                            />
                            <div
                                class="history-query-title-search__search-icon"
                                @click="searchHandler"
                            >
                                <div class="history-query-title-search__search-icon-mask" />
                                <PtIcon icon="SolidSearchLeft"></PtIcon>
                            </div>
                        </div>
                    </div>

                    <!-- 表格 -->
                    <HistoryCommonTable
                        ref="historyTable"
                        :columns="currentColumns"
                        :result="result"
                        :total="total"
                        :limit="limit"
                        :resultLength="resultLength"
                        :isCommonTable="isCommonTable"
                        :currentProject="currentProject"
                        :activeTab="activeTab"
                        :isLoading="isLoading"
                        @changeTableResult="changeTableResult"
                        @deleteHistoryHandler="deleteHistoryHandler"
                    />
                    <!-- 翻页 -->
                    <HistoryTablePagination
                        v-if="total"
                        :total="total"
                        :currentPage="parseInt(currentPage)"
                        :pageSize="20"
                        :pageSizes="[20]"
                        :disabled="isLoading"
                        @current-change="changePageHandler"
                    />
                </TabPane>
            </template>
        </Tabs>
        <!-- 删除所有弹框 -->
        <ComfirmDialog
            v-if="showDeleteAllDialog"
            :title="$t('history.deleteDialog.delete')"
            :desc="$t('history.deleteDialog.' + isDeleteKey)"
            :confirm-text="$t('history.deleteDialog.delete')"
            @closeHandler="showDeleteAllDialog = false"
            @confrimHandler="deleteAllHistoryConfrimHandler"
        />
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { Tabs, TabPane } from 'element-ui';
import { findIndex, map, forEach, get, isEmpty, includes } from 'lodash';
import { Icon, Toast } from 'patsnap-biz';
import "@patsnap-ui/icon/assets/solid/delete.svg";
import "@patsnap-ui/icon/assets/solid/SearchLeft.svg";
import dayjs from 'dayjs';
import TitleSearch from '../components/CommonComponent/TitleSearch';
import HistoryTablePagination from '../components/HistoryCommonTable/HistoryTablePagination.vue'; // 分页组件
import { getDefaultRangeDate } from '../config/config';
import { AnalyticsTableColumns, DiscoveryTableColumns, ChemicalTableColumns, LoginTableColumns } from '../utils/tableConstant.js';
import { getHistoryApiImpl, deleteHistoryApiImpl } from '../api/impl/history.impl';
import { RavenInsertMeta } from 'library-@/services/sentry';
import { getAclCheckApi } from 'library-@/apis/core_basic_api/acl';
import { HISTORY_PERMISSIONS_MAP } from '../constants';
import qs from 'qs';
/* chemical自行计算时间 */
const autoDateRange = {
    '1': 30,
    '3': 90,
    '12': 365
};
const chemicalOptions = [
    {
        label: 'allSearch',
        value: 'ALL_SEARCH'
    },
    {
        label: 'ext',
        value: 'EXT'
    },
    {
        label: 'sim',
        value: 'SIM'
    },
    {
        label: 'sub',
        value: 'SUB'
    },
    {
        label: 'sup',
        value: 'SUP'
    },
    {
        label: 'combo',
        value: 'COMBO'
    },
    {
        label: 'fml',
        value: 'FML'
    },
    {
        label: 'd2p',
        value: 'D2P'
    },
    {
        label: 'property',
        value: 'PROPERTY'
    },
    {
        label: 'bulk',
        value: 'BULK'
    }
];
const chemicalMap = {
    all: '',
    search: 'ALL_SEARCH',
    extracted_chemicals: 'P2C',
    chemical_view: 'VIEW',
    analysis: 'CHEMSCAPE',
    export: 'EXPORT'
};
const chemicalFullHistory = 'FULL_HISTORY';
export default {
    components: {
        PtIcon: Icon,
        TitleSearch,
        SortDropdown: () => import(/* webpackChunkName: "SortDropdown" */ '../components/CommonComponent/SortDropdown'),
        ChemicalSortDropDown: () => import(/* webpackChunkName: "ChemicalSortDropDown" */ '../components/Chemical/ChemicalSortDropDown.vue'),
        HistoryCommonTable: () => import(/* webpackChunkName: "HistoryCommonTable" */ '../components/HistoryCommonTable/HistoryCommonTable'),
        ComfirmDialog: () => import(/* webpackChunkName: "ComfirmDialog" */ '../components/CommonComponent/Dialog/ComfirmDialog'),
        Tabs,
        TabPane,
        HistoryTablePagination
    },
    data() {
        const tabTypes = {
            analytics: ['all', 'search', 'view', 'analysis', 'export', 'file_wrapper', 'export_paper'],
            discovery: ['search', 'export'],
            chemical: ['all', 'search', 'extracted_chemicals', 'chemical_view', 'analysis', 'export'],
            login: ['login']
        };
        const initRequestParams = {
            ...qs.parse(window.location.search.substring(1)),
            ...qs.parse(window.location.hash.split('?')[1] || '')
        };
        const { keyword, startDate, endDate, duration, historyType, from_site } = initRequestParams;
        const defaultDate = {
            start: Number(startDate),
            end: Number(endDate)
        };
        return {
            tabTypes /* tab项根据左侧项目切换 */,
            activeTab: '' /* 当前选中tab */,
            duration,
            chemicalOptions,
            autoDateRange,
            chemicalMap,
            limit: 0,
            historyType,
            from_site /* 埋点用记录history来源*/,
            keyword,
            placeholderText: this.$t('history.placeholder'),
            startDate: Number(startDate),
            endDate: Number(endDate),
            defaultDate,
            result: [],
            options: [],
            total: 0,
            isLoading: false,
            showDeleteAllDialog: false,
            addWorkSpacePermission: false
        };
    },
    computed: {
        /* historySelectedList:已选中history记录，支持分页保存 */
        ...mapGetters(['historySelectedList']),
        /* 判断是否为login,增加覆盖样式 */
        isLogin() {
            return this.currentProject === 'login';
        },
        /* chemical的search下出现搜索下拉框 */
        isShowAllType() {
            return this.currentProject === 'chemical' && this.activeTab === 'search';
        },
        isDeleteKey() {
            if (this.activeTab === 'export' && this.currentProject === 'analytics') {
                return 'patent_export';
            }
            return this.activeTab;
        },
        /* search是单层接口, 其他是双层接口 */
        isCommonTable() {
            return this.currentProject !== 'analytics' && ['search'].includes(this.activeTab);
        },
        showSearchInput() {
            return this.currentProject !== 'chemical' && ['search'].includes(this.activeTab);
        },
        currentTitle() {
            const history_permission = window.zPREFACE.history_permission;
            const isShowEurekaInsights = history_permission.eureka_insights && !history_permission.discovery;

            const titleObj = {
                analytics: 'analytics_history',
                discovery: isShowEurekaInsights ? 'eureka_insights_history' : 'discovery_history',
                chemical: 'chemical_history',
                login: 'login_history'
            };
            return titleObj[this.currentProject] || '';
        },
        currentProject() {
            return this.$route.params.type;
        },
        /* 当前tab list */
        currentTab() {
            return this.tabTypes[this.currentProject] || [];
        },
        currentPage() {
            return String(this.$route.params.page || '1');
        },
        /* 当前table的columns */
        currentColumns() {
            const columnsObj = {
                analytics: AnalyticsTableColumns,
                discovery: DiscoveryTableColumns,
                chemical: ChemicalTableColumns,
                login: LoginTableColumns
            };
            return columnsObj[this.currentProject][this.activeTab];
        },
        /* 获取结果长度，用于判断全选是否选中 */
        resultLength() {
            let _length = 0;
            if (this.isCommonTable) {
                _length = this.result.length;
            } else if (this.result.length) {
                _length = this.result.reduce((pre, cur) => {
                    return pre + cur.data.length;
                }, 0);
            }
            return _length;
        }
    },
    watch: {
        currentProject() {
            this.keyword = '';
        },
        activeTab() {
            this.resetData();
        },
        $route: {
            immediate: true,
            handler() {
                this.activeTab = this.currentTab[this.$route.params.tab];
                this.init();
            }
        },
    },
    created() {
        RavenInsertMeta('History', 'Click History', {
            from: this.from_site
        });
    },
    beforeRouteUpdate(to, from, next) {
        const type = to.params.type;
        const history_permission = window.zPREFACE.history_permission;
        const types = ['analytics', 'discovery', 'chemical'];
        const isLogin = type === 'login';
        if (isLogin || (includes(types, type) && history_permission[HISTORY_PERMISSIONS_MAP[type]]) || (type === 'discovery' && history_permission.eureka_insights)) {
            next();
        } else {
            this.$router.go(0);
        }
    },
    methods: {
        ...mapActions(['updateHistorySelectedList', 'updateWorkSpacePermission']),
        init() {
            const options = [
                {
                    label: 'one',
                    value: '1',
                    icon: 'SolidSortMaxMin'
                },
                {
                    label: 'three',
                    value: '3',
                    icon: 'SolidSortMaxMin'
                },
                {
                    label: 'year',
                    value: '12',
                    icon: 'SolidSortMaxMin'
                },
                {
                    label: 'period',
                    value: '0',
                    icon: 'SolidSortMaxMin'
                },
            ];

            this.duration = this.duration || '1';
            let selectedIndex = findIndex(options, { value: this.duration });
            selectedIndex = selectedIndex === -1 ? 0 : selectedIndex;
            options[selectedIndex].isInitSelected = true;
            this.options = options;
            const params = this.getApiParams({
                page: this.currentPage,
                duration: this.duration
            });
            /* 判断是否有保存工作空间操作权限 */
            if (this.currentProject === 'analytics' && this.activeTab === 'view') {
                this.getAcls();
            }
            this.getResult(params);
        },
        /* tab切换 */
        handleClick() {
            RavenInsertMeta('History', 'Toggle History Type', {
                Type: this.activeTab
            });
            const keyword = this.activeTab === 'search' ? this.keyword : '';
            let param = {
                page: 1,
                duration: this.duration,
                keyword,
                startDate: this.startDate,
                endDate: this.endDate
            };
            if (this.currentProject === 'chemical' && this.activeTab === 'search') {
                param.historyType = this.historyType;
            }
            this.updateUrl(param);
        },
        resetData() {
            this.result = [];
            this.total = 0;
        },
        getApiParams(oldparams) {
            const newParams = {
                tzOffset: dayjs().utcOffset(),
                page: oldparams.page,
                duration: oldparams.duration === 'full' ? '' : oldparams.duration,
                q: this.keyword || ''
            };
            if (newParams.duration === '0') {
                newParams.start = this.startDate;
                newParams.end = this.endDate;
            }
            return newParams;
        },
        /* 判断是否有保存工作空间操作权限 */
        async getAcls() {
            this.addWorkSpacePermission = get(await getAclCheckApi(['add_workspace']), 'permission.add_workspace', false);
            this.updateWorkSpacePermission(this.addWorkSpacePermission);
        },
        /* 获取history列表接口 */
        getResult({ page, duration }) {
            this.isLoading = true;
            const queryParams = this.getApiParams({ page, duration });
            /* 验证时间 */
            if (queryParams.duration === '0' && queryParams.start > queryParams.end) {
                Toast({
                    message: this.$t('history.invalidDate'),
                    type: 'warning',
                    showClose: true
                });
            }
            /* 参数清洗 */
            if (['chemical', 'discovery'].includes(this.currentProject)) {
                if (['1', '3', '12'].includes(duration)) {
                    queryParams.start = dayjs()
                        .subtract(this.autoDateRange[duration], 'days')
                        .valueOf();
                    queryParams.end = dayjs().valueOf();
                }
                if (this.currentProject === 'chemical') {
                    if (duration === 'full' || !duration) {
                        queryParams.time_type = chemicalFullHistory;
                    }
                    queryParams.page -= 1;
                    queryParams.history_type =
                        this.activeTab === 'search' ? this.historyType || 'ALL_SEARCH' : this.chemicalMap[this.activeTab];
                }
            } else {
                /* analytics的type为all时不用传 */
                queryParams.type = this.activeTab !== 'all' ? this.activeTab : '';
            }
            getHistoryApiImpl({
                projectType: this.currentProject,
                currentTab: this.activeTab,
                params: queryParams
            })
                .then(res => {
                    const { total, history_maps, limit } = res;
                    this.result = history_maps || [];
                    this.total = total;
                    this.limit = limit;
                    /* 根据historySelectedList将数据重新选中 */
                    this.washData();
                    if (!this.result.length && String(queryParams.page) !== '1' && total) {
                        this.updateUrl({
                            page: '1',
                            duration: this.duration || '1',
                            keyword: this.keyword,
                            startDate: this.startDate,
                            endDate: this.endDate
                        });
                    } else {
                        this.isLoading = false;
                    }
                })
                .catch(e => {
                    console.error(e);
                    this.result = [];
                    this.total = 0;
                    this.isLoading = false;
                });
        },
        /* 更新URL */
        updateUrl({ page, duration, keyword, startDate, endDate, historyType }) {
            const params = {
                duration,
                keyword,
                startDate,
                endDate,
                historyType
            };
            this.defaultDate = {
                start: startDate,
                end: endDate
            };
            this.duration = duration;
            const currentTabIndex = this.currentTab.findIndex(item => item === this.activeTab);
            const newHash = `/${this.currentProject}/${currentTabIndex}/${page}?${qs.stringify(params)}`;
            this.$router.push(newHash);
        },
        /* 分页方法 */
        changePageHandler(page) {
            this.updateUrl({
                page,
                duration: this.duration,
                keyword: this.keyword,
                startDate: this.startDate,
                endDate: this.endDate
            });
        },
        /* search搜索框方法 */
        searchHandler() {
            this.updateHistorySelectedList([]);
            this.updateUrl({
                page: '1',
                duration: this.duration,
                keyword: this.keyword,
                startDate: this.startDate,
                endDate: this.endDate,
            });
        },
        /* 选择时间范围方法 */
        dropdownChangeHandler(dropdownInfo) {
            this.updateHistorySelectedList([]);
            if (dropdownInfo.value === '0') {
                const defaultDate = getDefaultRangeDate();
                this.startDate = dropdownInfo.start || defaultDate.start;
                this.endDate = dropdownInfo.end || defaultDate.end;
            } else {
                this.startDate = '';
                this.endDate = '';
            }
            this.updateUrl({
                page: '1',
                duration: dropdownInfo.value,
                keyword: this.keyword,
                startDate: this.startDate,
                endDate: this.endDate
            });
        },
        /* Chemical search类型筛选 */
        allTypeChangeHandler(val) {
            this.historyType = val;
            this.updateUrl({
                page: '1',
                duration: this.duration,
                keyword: this.keyword,
                startDate: this.startDate,
                endDate: this.endDate,
                historyType: this.historyType
            });
        },
        openDeleteAllDialog() {
            this.showDeleteAllDialog = true;
        },
        /* 删除方法(全删、单独删通用) */
        deleteAction({ ids = [], deleteAll = false }) {
            let params = {
                analytics: {
                    type: this.activeTab,
                    historyIds: ids
                },
                /* discovery全删不传参数 */
                discovery: {
                    ids: ids,
                },
                chemical: {
                    delete_all: deleteAll && this.activeTab === 'all',
                    history_type: deleteAll && this.activeTab !== 'all' && isEmpty(ids) ? this.chemicalMap[this.activeTab] : null,
                    history_ids: ids
                },
                login: {
                    type: this.activeTab,
                    historyIds: ids
                },
            };
            let list = {
                deleteAll,
                projectType: this.currentProject,
                currentTab: this.activeTab,
                params: this.currentProject === 'discovery' && deleteAll ? {} : params[this.currentProject]
            };
            this.isLoading = true;
            if (this.currentProject === 'chemical' && list.deleteAll) {
                delete list.params.history_ids;
            } else {
                delete list.params.history_type;
            }
            deleteHistoryApiImpl(list)
                .then(() => {
                    Toast({
                        message: this.$t('saveQuery.deleteSuccess'),
                        type: 'success',
                        showClose: true
                    });

                    let newSelectedList = [];
                    if (!deleteAll) {
                        newSelectedList = map(this.historySelectedList, item => item);
                        forEach(ids, id => {
                            const index = findIndex(newSelectedList, { id });
                            if (index !== -1) {
                                newSelectedList.splice(index, 1);
                            }
                        });
                    }
                    this.updateHistorySelectedList(newSelectedList);
                    this.updateUrl({
                        /* 删除全部回到第一页 */
                        page: deleteAll ? 1 : this.currentPage,
                        duration: this.duration,
                        keyword: this.keyword,
                        startDate: this.startDate,
                        endDate: this.endDate
                    });
                    this.getResult({ page: deleteAll ? 1 : this.currentPage, duration: this.duration });
                })
                .catch(e => {
                    Toast({
                        message: e._error_msg,
                        type: 'error',
                        showClose: true
                    });
                    this.isLoading = false;
                });
        },
        deleteAllHistoryConfrimHandler() {
            this.showDeleteAllDialog = false;
            /* 不传参数, 删除全部 */
            this.deleteAction({ ids: [], deleteAll: true });
        },
        deleteHistoryHandler(ids) {
            /* 传参数, 删除部分 */
            this.deleteAction({ ids: ids });
        },
        // 表格全选或反选
        changeTableResult(isSelectAll) {
            this.result.forEach(item => {
                if (this.isCommonTable) {
                    this.$set(item, 'isSelected', isSelectAll);
                } else {
                    item.data.forEach(rowItem => {
                        this.$set(rowItem, 'isSelected', isSelectAll);
                    });
                }
            });
        },
        /* 根据historySelectedList将数据重新选中 */
        washData() {
            if (this.isCommonTable) {
                forEach(this.result, item => {
                    const index = findIndex(this.historySelectedList, { id: item.id });
                    item.isSelected = index !== -1;
                });
            } else {
                forEach(this.result, item => {
                    forEach(item.data, rowItem => {
                        const index = findIndex(this.historySelectedList, { id: rowItem.id });
                        rowItem.isSelected = index !== -1;
                    });
                });
            }
        }
    }
};
</script>

<style lang="scss">
.search-history-view {
    position: relative;
    margin-right: 0;
    padding-top: 70px;
    padding-left: 40px;
    padding-bottom: 0;
    height: 100%;
    width: calc(100% - 40px);
    box-sizing: border-box;
    flex-grow: 1;
    .el-tabs {
        position: relative;
        height: 100%;
        .el-tabs__header {
            position: absolute;
            width: calc(100% - 40px);
            margin: 0;
            z-index: 1;
            .el-tabs__nav-wrap {
                &::after {
                    background-color: #dfe1e6;
                }
            }
            .el-tabs__active-bar {
                background-color: $green-primary;
            }
            .el-tabs__item {
                font-size: 16px;
                color: $text-secondary;
                font-weight: normal;
                &:hover {
                    color: $green-text;
                }
                &.is-active {
                    color: $green-text;
                    font-weight: bold;
                }
            }
        }
        .el-tabs__content {
            height: 100%;
        }
        .el-tab-pane {
            position: relative;
            padding-top: 103px;
            padding-bottom: 40px;
        }
    }
    .history-query-title-search {
        position: absolute;
        top: 24px;
        left: 40px;
        right: 40px;
    }
    .search-history-view__delete-all {
        top: 55px;
    }
    .search-history-view_login-tab {
        .el-tabs__header {
            display: none;
        }
        .search-history-view__delete-all,
        .search-history-view__sort-filter {
            top: 0;
        }
        .el-tab-pane {
            padding-top: 48px;
        }
    }
    &__delete-all {
        height: 32px;
        line-height: 30px;
        background: #fff;
        border-radius: 4px;
        padding-left: 36px;
        padding-right: 15px;
        border: 1px solid $border-normal;
        display: inline-block;
        position: absolute;
        top: 68px;
        left: 0;
        font-size: 14px;
        color: $text-primary;
        &:hover {
            background: $bg-normal;
            cursor: pointer;
        }
        .patsnap-biz-icon {
            position: absolute;
            left: 8px;
            top: 50%;
            transform: translateY(-50%);
        }
    }
    .search-history-view__sort-filter {
        position: absolute;
        top: 55px;
        right: 40px;
        display: flex;
    }
    .history-query-title-search__search {
        float: right;
        margin-left: 16px;
        position: relative;
        input {
            width: 220px;
            height: 32px;
            line-height: 32px;
            padding: 0 36px 0 8px;
            font-size: 14px;
            border: 1px solid #b3bac5;
            border-radius: 4px;
            color: #061632;
            &:focus {
                border-color: $green-primary;
                // box-shadow:  0 0 0 2px #b9d5f1;
            }
            &::placeholder {
                color: #b3bac5;
            }
        }
        &-icon {
            position: absolute;
            right: 4px;
            top: 50%;
            transform: translateY(-50%);
            transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out,
                border 0.15s ease-in-out;
            border-radius: 4px;
            &-mask {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                z-index: 1;
            }
            &:hover {
                background-color: #ebecf0;
                cursor: pointer;
            }
        }
    }
}
</style>
